// src/pages/admin/AtRiskStudents.jsx
// Phase 8 — At-risk student detection dashboard.
// Shows students whose attendance rate falls below a configurable threshold.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../../api";
import ListPageLayout, { ListPageHeader, ListLoadingSpinner } from "../../components/layout/ListPageLayout";
import PaginationFooter from "../../components/common/PaginationFooter";
import SortableTableHeader from "../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../hooks/useSortedClientPagination";
import { atRiskSortColumns } from "../../config/tableSortColumns";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import UserLink from "../../components/common/UserLink";
import { userProfilePath } from "../../utils/userRoutes";
import { TextInput } from "../../components/common/form";
import { formatDate } from "../../utils/regionalFormat";
import { localizeBatchName } from '../../utils/displayNames';
import {
    ChartPanel,
    CompositionDonut,
    CompareBar,
    CHART_COLORS,
} from "../../components/charts";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fullName = (row, tr) =>
    [row.firstName, row.middleName, row.lastName].filter(Boolean).join(" ").trim() || tr("pages.atRisk.studentFallback", { id: row.studentId });

const initials = (row) => {
    const parts = [row.firstName, row.lastName].filter(Boolean);
    return parts.map(p => p[0].toUpperCase()).join("") || "?";
};

const formatLastSeen = (iso, tr) => {
    if (!iso) return tr("common.never");
    return formatDate(iso) || iso;
};

const avatarColor = (id) => {
    const colors = [
        "bg-red-500", "bg-orange-500", "bg-amber-500",
        "bg-indigo-500", "bg-violet-500", "bg-rose-500",
    ];
    return colors[id % colors.length];
};

// ─── Rate bar ─────────────────────────────────────────────────────────────────
function RateBar({ rate }) {
    const pct = Math.min(100, Math.max(0, Number(rate) || 0));
    const color = pct < 25 ? "bg-red-500"
        : pct < 50 ? "bg-orange-400"
            : "bg-amber-400";
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden min-w-[60px]">
                <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className={`text-sm font-semibold tabular-nums w-12 text-right ${pct < 25 ? "text-red-600" : pct < 50 ? "text-orange-600" : "text-amber-600"
                }`}>{pct.toFixed(1)}%</span>
        </div>
    );
}

// ─── Risk badge ───────────────────────────────────────────────────────────────
function RiskBadge({ rate }) {
    const { t: tr } = useTranslation();
    if (rate < 25) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">🔴 {tr("pages.atRisk.riskCritical")}</span>;
    if (rate < 50) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">🟠 {tr("pages.atRisk.riskHigh")}</span>;
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">🟡 {tr("pages.atRisk.riskMedium")}</span>;
}

// ─── Student row ──────────────────────────────────────────────────────────────
function StudentRow({ row, navigate }) {
    const { t: tr } = useTranslation();
    const name = fullName(row, tr);
    return (
        <tr className="hover:bg-gray-50 transition-colors">
            {/* Avatar + name */}
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${avatarColor(row.studentId)}`}>
                        {initials(row)}
                    </span>
                    <div>
                        <UserLink
                            id={row.studentId}
                            name={name}
                            className="text-sm font-semibold text-gray-900 hover:text-primary-600"
                        />
                        {row.email && (
                            <p className="text-xs text-gray-400 truncate max-w-[200px]">{row.email}</p>
                        )}
                    </div>
                </div>
            </td>

            {/* Risk level */}
            <td className="px-4 py-3">
                <RiskBadge rate={row.attendanceRate} />
            </td>

            {/* Rate bar */}
            <td className="px-4 py-3 min-w-[180px]">
                <RateBar rate={row.attendanceRate} />
            </td>

            {/* Sessions */}
            <td className="px-4 py-3">
                <span className="text-sm text-gray-700 tabular-nums font-medium">
                    {row.sessionsAttended}
                    <span className="text-gray-400 font-normal"> / {row.totalSessions}</span>
                </span>
            </td>

            {/* Last seen */}
            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                {formatLastSeen(row.lastSeenIso, tr)}
            </td>

            {/* Batches */}
            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px]">
                <span className="truncate block" title={(row.batchNames || '').split(',').map((n) => localizeBatchName(n.trim(), tr)).filter(Boolean).join(', ') || undefined}>{(row.batchNames || '').split(',').map((n) => localizeBatchName(n.trim(), tr)).filter(Boolean).join(', ') || "—"}</span>
            </td>

            {/* Actions */}
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(userProfilePath(row.studentId))}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                        {tr("pages.atRisk.profile")}
                    </button>
                    {row.email && (
                        <a
                            href={`mailto:${row.email}?subject=Your%20attendance%20needs%20attention&body=Hi%20${encodeURIComponent(row.firstName || name)}%2C%0A%0AWe%20noticed%20your%20attendance%20rate%20has%20dropped%20to%20${row.attendanceRate}%25.%20Please%20reach%20out%20if%20you%20need%20any%20support.%0A%0AKind%20regards`}
                            className="text-xs px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                        >
                            {tr("pages.atRisk.emailAction")}
                        </a>
                    )}
                </div>
            </td>
        </tr>
    );
}

// ─── Filter defaults & URL parsing ────────────────────────────────────────────

const DEFAULT_DAYS = 30;
const DEFAULT_THRESHOLD = 50;

function parseDays(raw) {
    const n = parseInt(raw, 10);
    return !Number.isNaN(n) && n >= 1 && n <= 365 ? n : DEFAULT_DAYS;
}

function parseThreshold(raw) {
    const n = parseInt(raw, 10);
    return !Number.isNaN(n) && n >= 1 && n <= 100 ? n : DEFAULT_THRESHOLD;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AtRiskStudents() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const days = parseDays(searchParams.get("days") ?? DEFAULT_DAYS);
    const threshold = parseThreshold(searchParams.get("threshold") ?? DEFAULT_THRESHOLD);

    const [draftDays, setDraftDays] = useState(String(days));
    const [draftThreshold, setDraftThreshold] = useState(String(threshold));

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchRef = useRef(0);

    // Keep draft inputs in sync when URL changes (e.g. browser back/forward)
    useEffect(() => {
        setDraftDays(String(days));
        setDraftThreshold(String(threshold));
    }, [days, threshold]);

    const load = useCallback(async () => {
        const token = ++fetchRef.current;
        setLoading(true);
        setError("");
        try {
            const res = await API.get("/admin/at-risk", { params: { days, threshold } });
            if (token !== fetchRef.current) return; // stale
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch {
            if (token !== fetchRef.current) return;
            setError(t("pages.atRisk.loadFailed"));
        } finally {
            if (token === fetchRef.current) setLoading(false);
        }
    }, [days, threshold]);

    useEffect(() => { load(); }, [load]);

    const pagination = useSortedClientPagination(rows, {
        columns: atRiskSortColumns,
        resetKey: `${days}-${threshold}`,
    });

    const applyFilters = () => {
        const d = parseInt(draftDays, 10);
        const tVal = parseInt(draftThreshold, 10);
        if (isNaN(d) || d < 1 || d > 365) { setError(t("pages.atRisk.daysError")); return; }
        if (isNaN(tVal) || tVal < 1 || tVal > 100) { setError(t("pages.atRisk.thresholdError")); return; }
        setError("");
        setSearchParams({ days: String(d), threshold: String(tVal) }, { replace: true });
    };

    // Counts by risk tier
    const critical = rows.filter(r => r.attendanceRate < 25).length;
    const high = rows.filter(r => r.attendanceRate >= 25 && r.attendanceRate < 50).length;
    const medium = rows.filter(r => r.attendanceRate >= 50).length;

    const tierBadges = !loading && rows.length > 0 ? (
        <>
            {critical > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-red-50 text-red-700 text-sm font-semibold border border-red-100">
                    🔴 {t("pages.atRisk.tierCritical", { count: critical })}
                </span>
            )}
            {high > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-orange-50 text-orange-700 text-sm font-semibold border border-orange-100">
                    🟠 {t("pages.atRisk.tierHigh", { count: high })}
                </span>
            )}
            {medium > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold border border-amber-100">
                    🟡 {t("pages.atRisk.tierMedium", { count: medium })}
                </span>
            )}
        </>
    ) : null;

    return (
        <ListPageLayout className="min-h-full">
            <ListPageHeader
                eyebrow={t("pages.atRisk.eyebrow")}
                title={t("pages.atRisk.title")}
                subtitle={t("pages.atRisk.subtitle")}
                actions={tierBadges}
            >
                <div className="flex items-end gap-1.5">
                    <TextInput
                        label={t("pages.atRisk.daysLabel")}
                        type="number"
                        min={1}
                        max={365}
                        value={draftDays}
                        onChange={(e) => setDraftDays(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                        className="page-hero-toolbar-input"
                        fieldClassName="flex flex-col gap-1"
                    />
                    <span className="text-sm text-slate-500 pb-2">{t("pages.atRisk.daysSuffix")}</span>
                </div>

                <div className="flex items-end gap-1.5">
                    <TextInput
                        label={t("pages.atRisk.thresholdLabel")}
                        type="number"
                        min={1}
                        max={100}
                        value={draftThreshold}
                        onChange={(e) => setDraftThreshold(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                        className="page-hero-toolbar-input"
                        fieldClassName="flex flex-col gap-1"
                    />
                    <span className="text-sm text-slate-500 pb-2">{t("pages.atRisk.thresholdSuffix")}</span>
                </div>

                <button type="button" onClick={applyFilters} className="page-hero-toolbar-apply">
                    {t("common.apply")}
                </button>

                {loading && (
                    <LoadingSpinner size="xs" inline label={t("common.loading")} className="pb-2 !py-0 flex-row gap-1.5" />
                )}

                {error && (
                    <div className="w-full basis-full rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                        {error}
                    </div>
                )}
            </ListPageHeader>

            {!loading && rows.length > 0 && (
                <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ChartPanel
                        title={t("charts.atRiskTiers", { defaultValue: "Risk tiers" })}
                        height={220}
                    >
                        <CompositionDonut
                            data={[
                                { name: t("charts.tierCriticalShort", { defaultValue: "Critical" }), value: critical, color: CHART_COLORS.danger },
                                { name: t("charts.tierHighShort", { defaultValue: "High" }), value: high, color: CHART_COLORS.warning },
                                { name: t("charts.tierMediumShort", { defaultValue: "Medium" }), value: medium, color: CHART_COLORS.warningSoft },
                            ].filter((d) => d.value > 0)}
                            chartSize={180}
                            centerValue={rows.length}
                            centerLabel={t("charts.students", { defaultValue: "Students" })}
                        />
                    </ChartPanel>
                    <ChartPanel
                        title={t("charts.attendanceRates", { defaultValue: "Attendance rates" })}
                        subtitle={t("charts.atRiskRateHint", { defaultValue: "Lowest rates in the current list" })}
                        height={220}
                    >
                        <CompareBar
                            data={[...rows]
                                .sort((a, b) => (a.attendanceRate ?? 0) - (b.attendanceRate ?? 0))
                                .slice(0, 8)
                                .map((r) => ({
                                    name: fullName(r, t).slice(0, 14),
                                    value: Math.round(Number(r.attendanceRate ?? 0)),
                                }))}
                            height={200}
                            layout="horizontal"
                            colorByIndex={false}
                            yFormatter={(v) => `${v}%`}
                        />
                    </ChartPanel>
                </div>
            )}

            <div className="flex-1">
                {loading && rows.length === 0 && !error ? (
                    <ListLoadingSpinner />
                ) : null}
                {!loading && !error && rows.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="text-5xl mb-4">🎉</div>
                        <h3 className="text-lg font-semibold text-gray-700">{t("pages.atRisk.emptyTitle")}</h3>
                        <p className="text-sm text-gray-400 mt-1 max-w-xs">
                            {t("pages.atRisk.emptyDescription", { threshold, days })}
                        </p>
                    </div>
                )}

                {rows.length > 0 && (
                    <div className="list-table-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <SortableTableHeader label={t("pages.atRisk.colStudent")} columnKey="student" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide" />
                                        <SortableTableHeader label={t("pages.atRisk.colRisk")} columnKey="risk" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide" />
                                        <SortableTableHeader label={t("pages.atRisk.colAttendanceRate")} columnKey="rate" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide min-w-[200px]" />
                                        <SortableTableHeader label={t("pages.atRisk.colSessions")} columnKey="sessions" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide" />
                                        <SortableTableHeader label={t("pages.atRisk.colLastSeen")} columnKey="lastSeen" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide" />
                                        <SortableTableHeader label={t("pages.atRisk.colBatches")} columnKey="batches" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide" />
                                        <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">{t("common.actions")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {pagination.pageItems.map(row => (
                                        <StudentRow key={row.studentId} row={row} navigate={navigate} />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {pagination.total > 0 && (
                            <PaginationFooter
                                page={pagination.page}
                                size={pagination.size}
                                total={pagination.total}
                                onPageChange={pagination.setPage}
                                onSizeChange={pagination.setSize}
                            />
                        )}

                        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
                            {t("pages.atRisk.footer", { count: rows.length, threshold, days })}
                        </div>
                    </div>
                )}
            </div>
        </ListPageLayout>
    );
}
